import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { flow, isEmpty } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildMakeModelDefaultYear,
  buildMmyModelFamilyIdsPath,
  buildModelFamilyIdsModelYearsPath,
  VehicleModel,
  VehicleEntities,
} from 'client/data/models/vehicle-v2';
import { getFilteredModelYears, getModelYearsWithReview } from 'site-modules/shared/utils/core-page/model-year-helpers';
import { getCurrentYear } from 'site-modules/shared/utils/time-util';
import { YearLinksList } from './year-links-list/year-links-list';

const YEARS_LIMIT = 3;

function OtherReviewYearsUI({
  vehicle: { year, model: { name: modelName }, make: { name: makeName } } = null,
  modelYears = null,
  defaultYear = null,
  className = null,
  heading = null,
  headingClassName = 'heading-5 mb-0_75',
  isMobile = false,
  hasSpeculationRule = false,
}) {
  const allModelYears = useMemo(() => {
    const currentYear = parseInt(getCurrentYear(), 10);
    return getModelYearsWithReview(
      getFilteredModelYears(modelYears, modelName).filter(
        ({ year: modelYear }) => parseInt(modelYear, 10) > currentYear - YEARS_LIMIT
      ),
      defaultYear
    );
  }, [modelYears, modelName, defaultYear]);

  return (
    <YearLinksList
      years={allModelYears}
      currentYear={year}
      makeName={makeName}
      modelName={modelName}
      className={className}
      heading={heading}
      headingClassName={headingClassName}
      isMobile={isMobile}
      hasSpeculationRule={hasSpeculationRule}
    />
  );
}

OtherReviewYearsUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  modelYears: PropTypes.arrayOf(VehicleEntities.ModelYearByFamilyId),
  defaultYear: PropTypes.number,
  className: PropTypes.string,
  heading: PropTypes.string,
  headingClassName: PropTypes.string,
  isMobile: PropTypes.bool,
  hasSpeculationRule: PropTypes.bool,
};

export const OtherReviewYears = flow(
  component =>
    connectToModel(component, {
      defaultYear: bindToPath(
        ({ vehicle }) => buildMakeModelDefaultYear({ make: vehicle.make.slug, model: vehicle.model.slug }),
        VehicleModel
      ),
      modelYears: bindToPath(
        ({ modelFamilyIds }) => !isEmpty(modelFamilyIds) && buildModelFamilyIdsModelYearsPath(modelFamilyIds),
        VehicleModel
      ),
    }),
  component =>
    connectToModel(component, {
      modelFamilyIds: bindToPath(
        ({ vehicle }) =>
          buildMmyModelFamilyIdsPath({ make: vehicle.make.slug, model: vehicle.model.slug, year: vehicle.year }),
        VehicleModel
      ),
    })
)(OtherReviewYearsUI);

export const OtherReviewYearsGraphql = connectToModel(OtherReviewYearsUI, {
  defaultYear: bindToPath(
    ({ vehicle }) => buildMakeModelDefaultYear({ make: vehicle.make.slug, model: vehicle.model.slug }),
    VehicleModel
  ),
  modelYears: bindToPath(({ vehicle }) => buildModelFamilyIdsModelYearsPath(vehicle.modelFamilyIds), VehicleModel),
});
